import { createContext, useContext, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { getRealityInfo } from "@/services/reality";
import type { RealityInfo } from "@/services/reality/types/reality.types";
import { queryClient } from "@/services/react-query";
import { isBefore } from "date-fns";
import { isDevSubscriptionBypassEnabled } from "@/utils/dev-subscription-bypass";

type RealityContext = {
  isSubscriptionOpen: boolean;
  realityInfo: RealityInfo | undefined;
  isRealityInfoLoading: boolean;
};

const RealityContext = createContext<RealityContext>({} as RealityContext);

type RealityProviderProps = {
  children: React.ReactNode;
};

export const RealityProvider = ({ children }: RealityProviderProps) => {
  const { data: realityInfo, isLoading } = useQuery(
    ["reality-info"],
    () => getRealityInfo(),
    { retry: true, staleTime: 0, refetchOnMount: true, refetchOnWindowFocus: true }
  );

  /** Sign-in / sign-up: always network fetch for subscription deadline (no stale cache). */
  useEffect(() => {
    const refetch = () => {
      void queryClient.fetchQuery(["reality-info"], getRealityInfo, {
        staleTime: 0,
      });
    };
    refetch();
    const onFocus = () => refetch();
    const onVisible = () => {
      if (document.visibilityState === "visible") refetch();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const shouldBypassSubscriptionLock = useMemo(() => {
    // Dev-only escape hatch: localStorage.setItem("devBypassSubscriptionLock", "true")
    return isDevSubscriptionBypassEnabled();
  }, []);

  const isSubscriptionOpen = useMemo(() => {
    if (shouldBypassSubscriptionLock) {
      return true;
    }

    if (isLoading || !realityInfo?.finalSubscriptionDate) {
      return true;
    }

    return !isBefore(realityInfo.finalSubscriptionDate, new Date());
  }, [isLoading, realityInfo?.finalSubscriptionDate, shouldBypassSubscriptionLock]);

  return (
    <RealityContext.Provider
      value={{
        isSubscriptionOpen,
        realityInfo,
        isRealityInfoLoading: isLoading,
      }}
    >
      {children}
    </RealityContext.Provider>
  );
};

export const useReality = (): RealityContext => useContext(RealityContext);
